import { Howl } from "howler";
import type { LiveMetadata } from "shared";
import { useGame } from "../store";

/**
 * Audio orchestration, ported from legacy js/components/audio.js.
 *
 * Narration is streamed from /api/play_audio and captions/image-cues are
 * scheduled by their millisecond offsets relative to playback start. The shared
 * mutable `connectionDelay` measures how long the audio took to actually start,
 * and is reused by the socket layer to keep other visual cues in sync — exactly
 * as the original did.
 */
class AudioManager {
  private core?: Howl;
  private background?: Howl;
  private timers: number[] = [];
  private lastNotification = 0;

  /** Latency (ms) from requesting playback to it actually starting. */
  connectionDelay = 0;

  private clearTimers(): void {
    this.timers.forEach((t) => clearTimeout(t));
    this.timers = [];
  }

  private schedule(fn: () => void, ms: number): void {
    this.timers.push(window.setTimeout(fn, Math.max(0, ms)));
  }

  /** Play narration from the start, scheduling its captions and image cues. */
  playAudio(filename: string, meta: LiveMetadata): void {
    const start = Date.now();
    this.core?.stop();
    this.clearTimers();

    this.core = new Howl({
      src: [`/api/play_audio/${filename}`],
      autoplay: true,
      loop: false,
      volume: 1,
      html5: true,
      onplay: () => {
        this.connectionDelay = Date.now() - start;
        this.scheduleCues(meta, 0);
      },
      onend: () => this.background?.stop(),
      onplayerror: (_id, err) => console.error("Audio play error:", err),
    });
  }

  /** Join a track already in progress (catch-up), seeking near the end. */
  playAudioPartThrough(filename: string, secondsFromEnd: number, meta: LiveMetadata): void {
    this.core?.stop();
    this.clearTimers();

    const sound = new Howl({
      src: [`/api/play_audio/${filename}`],
      autoplay: true,
      loop: false,
      volume: 1,
      html5: true,
      onload: () => {
        sound.seek(Math.max(0, sound.duration() - secondsFromEnd));
      },
      onplay: () => {
        const positionMs = (sound.duration() - secondsFromEnd) * 1000;
        this.connectionDelay = positionMs;
        this.scheduleCues(meta, positionMs);
      },
    });
    this.core = sound;
  }

  /** Schedule captions/images at their offsets, showing the current one now. */
  private scheduleCues(meta: LiveMetadata, positionMs: number): void {
    const captions = meta.captions ?? [];
    const futureCaptions = captions.filter((c) => c.start > positionMs);
    const firstFuture = futureCaptions[0];
    if (firstFuture) {
      const idx = captions.indexOf(firstFuture);
      if (idx > 0) useGame.getState().setCaption(captions[idx - 1]!.text);
    }
    for (const c of futureCaptions) {
      this.schedule(() => useGame.getState().setCaption(c.text), c.start - positionMs);
    }

    const images = meta.images ?? [];
    const futureImages = images.filter((img) => img.start > positionMs);
    const firstFutureImg = futureImages[0];
    if (firstFutureImg) {
      const idx = images.indexOf(firstFutureImg);
      if (idx > 0) useGame.getState().setCueImage(images[idx - 1]!.image);
    }
    for (const img of futureImages) {
      this.schedule(
        () => useGame.getState().setCueImage(img.image),
        img.start - positionMs,
      );
    }
  }

  playBackgroundAudio(filename: string, level: number): void {
    this.background?.stop();
    this.background = new Howl({
      src: [`/api/play_audio/${filename}`],
      loop: true,
      volume: level / 2, // legacy halves the requested level
      html5: true,
      autoplay: true,
    });
  }

  /** New-message chime, rate-limited to once per 5s (legacy behaviour). */
  playNotification(): void {
    const now = Date.now();
    if (now - this.lastNotification < 5000) return;
    this.lastNotification = now;
    new Howl({ src: ["/audio/notification-old.mp3"], volume: 1 }).play();
  }

  /** Unlock the audio context during the pre-show tap; resolves on end/error. */
  testPlayback(): Promise<void> {
    return new Promise((resolve) => {
      const h = new Howl({
        src: ["/audio/notification-old.mp3"],
        html5: true,
        onend: () => resolve(),
        onplayerror: () => resolve(),
        onloaderror: () => resolve(),
      });
      h.play();
    });
  }
}

export const audio = new AudioManager();
